import { useMemo, useState, type FormEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type CotizacionDetalle, type Cronograma, type CuotaCotizacion } from "@/lib/api";
import { nombresTareas, opcionesRubro } from "@/lib/cronograma";
import {
  aPayloadCuota,
  armarPlan,
  avisoPorcentajes,
  formAtajo,
  formCuota,
  formatPorcentajeCuota,
  textoCuando,
  validarAtajo,
  validarCuota,
  type ErroresAtajo,
  type ErroresCuota,
  type FormAtajo,
  type FormCuota,
  type PayloadCuota,
  type TipoAtajo,
} from "@/lib/formaDePago";

const SIN_ERRORES = {};
type Dialogo =
  | { tipo: "cuota"; cuota: CuotaCotizacion | null }
  | { tipo: "borrar"; cuota: CuotaCotizacion }
  | { tipo: "atajo" }
  | { tipo: "sacar" }
  | null;

/**
 * Lógica de la Card "Forma de pago" de la ficha de un presupuesto recibido (sin JSX): los pagos del plan (concepto, %
 * del total y cuándo cae: en una fecha o según el cronograma) con su monto calculado por el backend, alta/edición/baja
 * de a uno y los atajos Acopio, Anticipo y saldo y Cuotas quincenales (en fechas o según un rubro), que reemplazan el
 * plan entero. Escribe el admin; los rubros e ítems para anclar salen del cronograma, que se pide al abrir un form.
 */
export function useFormaDePago(base: string, detalle: CotizacionDetalle | null, recargar: () => void) {
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [form, setForm] = useState<FormCuota>(() => formCuota());
  const [atajo, setAtajo] = useState<FormAtajo>(() => formAtajo("anticipo"));
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const { fid } = useContextoApp();
  const conForm = dialogo?.tipo === "cuota" || dialogo?.tipo === "atajo";
  const cronograma = useApiResource<Cronograma>(conForm ? `/api/fideicomisos/${fid}/cronograma` : null);
  const opciones = useMemo(() => {
    const c = cronograma.data;
    return {
      rubros: c ? opcionesRubro(c) : [],
      items: c ? [...nombresTareas(c)].map(([id, nombre]) => ({ valor: String(id), etiqueta: nombre })) : [],
    };
  }, [cronograma.data]);

  const accionGuardar = useAsyncAction((cuota: CuotaCotizacion | null, datos: PayloadCuota) =>
    cuota ? api.patch<CuotaCotizacion[]>(`${base}/cuotas/${cuota.id}`, datos) : api.post<CuotaCotizacion[]>(`${base}/cuotas`, datos),
  );
  const accionReemplazar = useAsyncAction((cuotas: PayloadCuota[]) => api.put<CuotaCotizacion[]>(`${base}/cuotas`, { cuotas }));
  const accionBorrar = useAsyncAction((cuotaId: number) => api.delete<CuotaCotizacion[]>(`${base}/cuotas/${cuotaId}`));

  const cuotas = detalle?.cuotas ?? [];
  const errores = validarCuota(form);
  const erroresAtajo = validarAtajo(atajo);
  const cerrar = () => setDialogo(null);
  const listo = () => {
    cerrar();
    recargar();
  };

  const abrirForm = (cuota: CuotaCotizacion | null) => {
    setForm(formCuota(cuota ?? undefined));
    setMostrarErrores(false);
    accionGuardar.limpiarError();
    setDialogo({ tipo: "cuota", cuota });
  };

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (dialogo?.tipo !== "cuota" || Object.keys(errores).length > 0) return;
    if (await accionGuardar.run(dialogo.cuota, aPayloadCuota(form))) listo();
  };

  const guardarAtajo = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (Object.keys(erroresAtajo).length > 0) return;
    if (await accionReemplazar.run(armarPlan(atajo))) listo();
  };

  return {
    cuotas,
    aviso: detalle ? avisoPorcentajes(cuotas.length, detalle.suma_porcentajes) : null,
    /** Los montos van en la moneda del presupuesto; en pesos se muestra también el USD. */
    moneda: detalle?.moneda ?? null,
    sinTotal: detalle !== null && detalle.monto === null,
    dialogo,
    cerrar,
    abrirNueva: () => abrirForm(null),
    abrirEditar: (c: CuotaCotizacion) => abrirForm(c),
    abrirBorrar: (c: CuotaCotizacion) => {
      accionBorrar.limpiarError();
      setDialogo({ tipo: "borrar", cuota: c });
    },
    abrirAtajo: (tipo: TipoAtajo) => {
      setAtajo(formAtajo(tipo));
      setMostrarErrores(false);
      accionReemplazar.limpiarError();
      setDialogo({ tipo: "atajo" });
    },
    abrirSacar: () => {
      accionReemplazar.limpiarError();
      setDialogo({ tipo: "sacar" });
    },
    form,
    cambiar: <K extends keyof FormCuota>(campo: K, valor: FormCuota[K]) => setForm((f) => ({ ...f, [campo]: valor })),
    errores: (mostrarErrores ? errores : SIN_ERRORES) as ErroresCuota,
    guardar,
    guardando: accionGuardar.loading,
    errorGuardar: accionGuardar.error,
    atajo,
    cambiarAtajo: <K extends keyof FormAtajo>(campo: K, valor: FormAtajo[K]) => setAtajo((f) => ({ ...f, [campo]: valor })),
    erroresAtajo: (mostrarErrores ? erroresAtajo : SIN_ERRORES) as ErroresAtajo,
    /** Vista previa de lo que va a quedar (sólo si el form del atajo es válido): "Cuota 3 de 3: 33,34 % · 01/12/2026". */
    previaAtajo: Object.keys(erroresAtajo).length === 0
      ? armarPlan(atajo).map((c) => {
          const rubro = opciones.rubros.find((o) => o.valor === atajo.rubroId)?.etiqueta;
          const cuando = c.ancla ? textoCuando({ ancla: c.ancla, fecha: c.fecha, dias: c.dias, rubro }) : c.fecha ? textoCuando({ ancla: "fecha", fecha: c.fecha }) : null;
          return `${c.concepto}: ${formatPorcentajeCuota(c.porcentaje)}${cuando ? ` · ${cuando}` : ""}`;
        })
      : [],
    /** Para anclar al cronograma: sus rubros y sus ítems ("Rubro › ítem"). */
    opcionesRubro: opciones.rubros,
    opcionesItem: opciones.items,
    cargandoCronograma: cronograma.loading,
    guardarAtajo,
    confirmarSacar: async () => {
      if (await accionReemplazar.run([])) listo();
    },
    reemplazando: accionReemplazar.loading,
    errorReemplazar: accionReemplazar.error,
    confirmarBorrar: async () => {
      if (dialogo?.tipo !== "borrar") return;
      if (await accionBorrar.run(dialogo.cuota.id)) listo();
    },
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
  };
}
